// Engine orchestrator: planner → swarm → synthesizer with audit trail,
// guardrails, pause/resume/stop, event bus, *domain routing* and
// *topic-drift detection*.

import { DEFAULT_CONFIG, DEFAULT_GUARDRAILS, type ResearchConfig } from "./config";
import { classifyQueryDomain, type DomainClassification } from "./domain";
import { detectTopicDrift } from "./topicDrift";
import { EventBus, type EngineListener } from "./events";
import { buildPlanTree, planLeaves, planSignature } from "./planner";
import { runSwarm } from "./swarm";
import { synthesizeDossier } from "./synthesizer";
import type {
  AuditEntry, AuditKind, DossierBadge, EngineStatus, Guardrails,
  PlanNode, PlanTree, ResearchRunState, SubAgentState,
} from "./types";
import { shortHash, uid } from "./util";

export interface RunOptions {
  query: string;
  guardrails?: Partial<Guardrails>;
  config?: Partial<ResearchConfig>;
  agents?: number;
  depth?: number;
  branching?: number;
}

export class DeepResearchEngine {
  readonly bus = new EventBus();
  private state: ResearchRunState;
  private controller: AbortController | null = null;
  private pausedResolvers: Array<() => void> = [];
  private isPaused = false;
  private cfg: ResearchConfig;

  constructor() {
    this.cfg = DEFAULT_CONFIG;
    this.state = this.blankState();
  }

  on(l: EngineListener) {
    return this.bus.on(l);
  }
  getState(): ResearchRunState {
    return this.state;
  }

  private blankState(): ResearchRunState {
    return {
      id: uid("run"),
      query: "",
      status: "idle",
      agents: {},
      audit: [],
      guardrails: { ...DEFAULT_GUARDRAILS },
      startedAt: 0,
      confidence: 0,
      risk: 0.18,
      tokensUsed: 0,
      costUsd: 0,
    };
  }

  async run(opts: RunOptions): Promise<ResearchRunState> {
    if (this.state.status !== "idle" && this.state.status !== "done" && this.state.status !== "stopped" && this.state.status !== "failed") {
      throw new Error("engine busy");
    }
    this.cfg = { ...DEFAULT_CONFIG, ...(opts.config ?? {}) } as ResearchConfig;
    this.controller = new AbortController();
    this.isPaused = false;
    this.state = {
      ...this.blankState(),
      query: opts.query,
      guardrails: { ...DEFAULT_GUARDRAILS, ...(opts.guardrails ?? {}) },
      startedAt: performance.now(),
      status: "planning",
    };
    this.bus.emit({ type: "engine:status", status: "planning" });

    // Time guardrail
    const timeoutId = window.setTimeout(() => {
      this.audit("guardrail.trip", { rule: "maxTimeSec", value: this.state.guardrails.maxTimeSec });
      this.stop();
    }, this.state.guardrails.maxTimeSec * 1000);

    try {
      // 0) Domain classification (drives planner personas + drift threshold)
      let domain: DomainClassification | undefined;
      try {
        domain = await classifyQueryDomain(opts.query, this.controller.signal);
        await this.audit("governance.check", {
          policy: "domain_classification",
          primary: domain.primary, secondary: domain.secondary,
          confidence: domain.confidence, source: domain.source,
        });
      } catch {
        // never fail the run on classification error
      }

      // 1) Plan
      const plan = await buildPlanTree({
        query: opts.query,
        domain,
        depth: opts.depth,
        branching: opts.branching,
        config: this.cfg,
        signal: this.controller.signal,
        onNode: (n) => {
          if (!this.state.plan) {
            this.state.plan = { rootId: n.id, nodes: { [n.id]: n }, maxDepth: opts.depth ?? this.cfg.planner.defaultDepth };
            this.bus.emit({ type: "plan:set", plan: this.state.plan });
          } else {
            this.state.plan.nodes[n.id] = n;
            this.bus.emit({ type: "plan:nodeUpdate", nodeId: n.id, patch: n });
          }
        },
        onAudit: async ({ kind, nodeId, payload }) => {
          await this.audit(kind, payload, { nodeId });
        },
      });
      this.state.plan = plan;
      const sig = await planSignature(plan);
      await this.audit("plan.create", { signature: sig, totalNodes: Object.keys(plan.nodes).length, leaves: planLeaves(plan).length });
      await this.audit("governance.check", { policy: "max_depth", limit: this.cfg.planner.maxDepth, used: plan.maxDepth });
      // stash domain on state so finalize can pass it to dossier
      (this.state as ResearchRunState & { _domain?: DomainClassification })._domain = domain;

      await this.waitIfPaused();
      if (this.controller.signal.aborted) return this.finalize("stopped");

      // 2) Swarm
      this.setStatus("swarming");
      const leaves = planLeaves(plan);
      const swarmAgents = await runSwarm({
        leaves,
        maxAgents: opts.agents ?? this.cfg.swarm.defaultAgents,
        signal: this.controller.signal,
        config: this.cfg,
        onSpawn: (a) => {
          this.state.agents[a.id] = a;
          this.bus.emit({ type: "swarm:spawn", agent: a });
          this.markNode(a.nodeId, { status: "running" });
        },
        onUpdate: (id, patch) => {
          const a = this.state.agents[id];
          if (!a) return;
          Object.assign(a, patch);
          this.bus.emit({ type: "swarm:agentUpdate", agentId: id, patch });
          if (patch.status === "done" || patch.status === "failed" || patch.status === "stopped") {
            this.markNode(a.nodeId, {
              status: patch.status === "done" ? "done" : patch.status === "failed" ? "failed" : "stopped",
              confidence: a.confidence,
            });
          }
          // Update running confidence/risk
          this.updateRunningMetrics();
        },
        onAudit: async ({ kind, agentId, nodeId, tool, latencyMs, payload }) => {
          await this.audit(kind as AuditKind, payload, { agentId, nodeId, tool, latencyMs });
        },
        onCost: (dt, dc) => {
          this.state.tokensUsed += dt;
          this.state.costUsd += dc;
          // Cost guardrail
          if (this.state.costUsd > this.state.guardrails.maxCostUsd) {
            this.audit("guardrail.trip", { rule: "maxCostUsd", spent: Number(this.state.costUsd.toFixed(4)) });
            this.stop();
          }
          if (this.state.tokensUsed > this.state.guardrails.maxTokens) {
            this.audit("guardrail.trip", { rule: "maxTokens", used: this.state.tokensUsed });
            this.stop();
          }
          this.bus.emit({
            type: "metrics",
            tokensUsed: this.state.tokensUsed,
            costUsd: this.state.costUsd,
            confidence: this.state.confidence,
            risk: this.state.risk,
          });
        },
      });

      await this.waitIfPaused();
      if (this.controller.signal.aborted) return this.finalize("stopped");

      // 3) Synthesize
      this.setStatus("synthesizing");
      await this.audit("synthesis", { agents: swarmAgents.length, totalClaims: swarmAgents.reduce((s, a) => s + a.claims.length, 0) });

      const dossier = synthesizeDossier({
        query: opts.query,
        plan,
        agents: swarmAgents,
        startedAt: this.state.startedAt,
        totalCostUsd: this.state.costUsd,
        totalTokens: this.state.tokensUsed,
      });

      // 3.5) Topic-drift detection on the assembled citations
      const badges: DossierBadge[] = [];
      try {
        const snippets = dossier.citations.map((c) => `${c.title}. ${c.snippet}`);
        if (snippets.length > 0) {
          const drift = await detectTopicDrift(opts.query, snippets, this.controller.signal);
          await this.audit("critique", {
            check: "topic_drift",
            avgSimilarity: Number(drift.avgSimilarity.toFixed(3)),
            offTopic: drift.offTopicCount, total: drift.totalCount, status: drift.status,
          });
          dossier.driftReport = {
            avgSimilarity: drift.avgSimilarity,
            offTopicCount: drift.offTopicCount,
            totalCount: drift.totalCount,
          };
          if (drift.status === "drift") {
            badges.push("TOPIC_DRIFT_DETECTED");
            // Penalize confidence by 30pts
            dossier.overallConfidence = Math.max(0, dossier.overallConfidence - 0.3);
            dossier.limitations.unshift("Topic drift detected: average similarity between citations and the original query is below the on-topic threshold (0.55). Confidence reduced by 30 points.");
          } else if (drift.status === "insufficient") {
            badges.push("INSUFFICIENT_SOURCES");
            dossier.limitations.unshift(`Insufficient on-topic sources: ${drift.offTopicCount}/${drift.totalCount} citations were off-topic. Re-run with a more specific query or different domain hints.`);
          }
        }
      } catch (driftErr) {
        await this.audit("critique", { check: "topic_drift", error: driftErr instanceof Error ? driftErr.message : String(driftErr) });
      }

      const stateWithDomain = this.state as ResearchRunState & { _domain?: DomainClassification };
      if (stateWithDomain._domain) {
        dossier.domain = {
          primary: stateWithDomain._domain.primary,
          secondary: stateWithDomain._domain.secondary,
          confidence: stateWithDomain._domain.confidence,
        };
      }
      if (badges.length) dossier.badges = badges;

      this.state.dossier = dossier;
      this.bus.emit({ type: "dossier", dossier });

      await this.audit("governance.check", {
        policy: "citation_coverage",
        citations: dossier.citations.length,
        sections: dossier.sections.length,
        badges,
      });

      window.clearTimeout(timeoutId);
      return this.finalize("done");
    } catch (e) {
      window.clearTimeout(timeoutId);
      if ((e as DOMException)?.name === "AbortError") return this.finalize("stopped");
      const msg = e instanceof Error ? e.message : String(e);
      this.state.error = msg;
      this.bus.emit({ type: "error", message: msg });
      return this.finalize("failed");
    }
  }

  pause() {
    if (this.state.status === "idle" || this.state.status === "done" || this.state.status === "failed" || this.state.status === "stopped") return;
    this.isPaused = true;
    this.audit("pause", { at: this.state.status });
    this.setStatus("paused");
  }

  resume() {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.audit("resume", {});
    // Restore status from last meaningful step (best-effort)
    this.setStatus(this.state.dossier ? "synthesizing" : "swarming");
    const resolvers = this.pausedResolvers.splice(0);
    for (const r of resolvers) r();
  }

  stop() {
    if (!this.controller) return;
    this.controller.abort();
    this.audit("stop", {});
    // Resume any paused waiters so they can settle through abort
    const resolvers = this.pausedResolvers.splice(0);
    for (const r of resolvers) r();
  }

  /** Replay-style rollback: rewind agents/dossier; keep audit log as evidence. */
  rollback() {
    this.audit("rollback", { rewoundAgents: Object.keys(this.state.agents).length });
    this.state.agents = {};
    this.state.dossier = undefined;
    this.state.confidence = 0;
    this.state.risk = 0.18;
    this.bus.emit({
      type: "metrics",
      tokensUsed: this.state.tokensUsed,
      costUsd: this.state.costUsd,
      confidence: 0,
      risk: 0.18,
    });
  }

  private async waitIfPaused() {
    if (!this.isPaused) return;
    await new Promise<void>((resolve) => this.pausedResolvers.push(resolve));
  }

  private setStatus(s: EngineStatus) {
    this.state.status = s;
    this.bus.emit({ type: "engine:status", status: s });
  }

  private finalize(s: EngineStatus): ResearchRunState {
    this.state.status = s;
    this.state.endedAt = performance.now();
    this.bus.emit({ type: "engine:status", status: s });
    return this.state;
  }

  private async audit(
    kind: AuditKind,
    payload: unknown,
    extra: { agentId?: string; nodeId?: string; tool?: import("./types").ToolHint; latencyMs?: number; costUsd?: number } = {},
  ) {
    const hash = await shortHash({ kind, payload, ...extra, ts: Date.now() });
    const entry: AuditEntry = {
      id: uid("au"),
      ts: Date.now(),
      kind,
      hash,
      ...extra,
      inputs: typeof payload === "object" ? payload : undefined,
      note: typeof payload === "string" ? payload : undefined,
    };
    this.state.audit.push(entry);
    this.bus.emit({ type: "audit", entry });
  }

  private markNode(nodeId: string, patch: Partial<PlanNode>) {
    const plan = this.state.plan;
    if (!plan) return;
    const node = plan.nodes[nodeId];
    if (!node) return;
    Object.assign(node, patch);
    this.bus.emit({ type: "plan:nodeUpdate", nodeId, patch });
  }

  private updateRunningMetrics() {
    const ags = Object.values(this.state.agents);
    if (!ags.length) return;
    const conf = ags.reduce((s, a) => s + a.confidence, 0) / ags.length;
    const failed = ags.filter((a) => a.status === "failed").length;
    const risk = Math.min(0.95, 0.1 + 0.6 * (failed / ags.length) + 0.3 * (1 - conf));
    this.state.confidence = conf;
    this.state.risk = risk;
    this.bus.emit({
      type: "metrics",
      tokensUsed: this.state.tokensUsed,
      costUsd: this.state.costUsd,
      confidence: conf,
      risk,
    });
  }
}

export type { ResearchRunState, PlanTree, SubAgentState, AuditEntry };
